import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, ReauthModal } from '../../components/ui';
import { motion } from 'framer-motion';
import { useAuth } from '@/core/AuthContext';
import type { ConfirmationResult } from 'firebase/auth';

export function DangerZone() {
  const navigate = useNavigate();
  const { userData, signOut, deleteAccount } = useAuth();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthConfirmation, setReauthConfirmation] = useState<ConfirmationResult | null>(null);
  const [verifying, setVerifying] = useState(false);

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Failed to delete account. Please try again.');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleReauthCodeSubmit = async (code: string) => {
    if (!reauthConfirmation) return;

    setVerifying(true);
    try {
      await reauthConfirmation.confirm(code);
      await deleteAccount();
      navigate('/login');
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Verification failed or account deletion failed. Please try again.');
      setVerifying(false);
      setShowReauthModal(false);
      setReauthConfirmation(null);
    }
  };

  const handleReauthCancel = () => {
    setShowReauthModal(false);
    setReauthConfirmation(null);
    setVerifying(false);
  };

  return (
    <>
      {/* Account Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Manage your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button
                variant="danger"
                data-testid="settings-sign-out-button"
                onClick={signOut}
                className="w-full sm:w-auto"
              >
                Sign Out
              </Button>
              <p className="text-xs text-gray-500 dark:text-slate-500">
                Need help? Contact support.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Danger Zone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card variant="elevated" padding="lg" className="border-2 border-red-200 dark:border-red-900/50">
          <CardHeader>
            <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
            <CardDescription>Irreversible actions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg">
                <h3 className="text-sm font-semibold text-red-900 dark:text-red-400 mb-1">
                  Delete Account
                </h3>
                <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                  Permanently delete your account and all associated data. This cannot be undone.
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  data-testid="settings-delete-button"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete My Account
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          data-testid="settings-delete-account-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            <Card padding="lg" variant="elevated" className="border-2 border-red-200 dark:border-red-900/50">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                    <svg
                      className="w-6 h-6 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      Delete Account?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      Are you sure? This cannot be undone.
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
                  <p className="text-sm text-red-900 dark:text-red-300">
                    <strong>This will permanently delete:</strong>
                  </p>
                  <ul className="mt-2 text-sm text-red-800 dark:text-red-400 space-y-1 list-disc list-inside">
                    <li>Your profile and account data</li>
                    <li>All workout history and progress</li>
                    <li>Any saved preferences</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    className="flex-1"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    className="flex-1"
                    data-testid="settings-confirm-delete-button"
                    onClick={handleDeleteConfirm}
                    isLoading={deleting}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Yes, Delete'}
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Re-authentication Modal */}
      <ReauthModal
        isOpen={showReauthModal}
        phoneNumber={userData?.phone || ''}
        onCodeSubmit={handleReauthCodeSubmit}
        onCancel={handleReauthCancel}
        isVerifying={verifying}
      />
    </>
  );
}
